import axios from 'axios'
import { getOrCreateDeviceId, getDeviceName } from '../utils/device'
import { createLogger } from '../utils/logger'

const logger = createLogger('DashboardServiceClient')

function getBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const port = window.location.port || ''
    const devPorts = new Set(['3000', '5173', '5174', '9083', '9084'])
    if (devPorts.has(port)) {
      return '/ds-api'
    }
    return `${window.location.origin}/api`
  }
  return '/ds-api'
}

export const dsClient = axios.create({
  headers: { 'Content-Type': 'application/json' },
})

dsClient.interceptors.request.use((config) => {
  if (!config.baseURL) {
    config.baseURL = getBaseUrl()
  }

  const token = sessionStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  try {
    config.headers['X-Device-Id'] = getOrCreateDeviceId()
    config.headers['X-Device-Name'] = getDeviceName()
  } catch (e) {
    logger.warn('Falha ao obter device info', e)
  }

  return config
})

// --- Tipos ---

export interface DashboardBundle {
  dashboard: {
    id: number
    codigo: string
    nome: string
    slug: string
    descricao: string
    tipo_dashboard: string
    tenant_scope: string
    owner_empresa_id: number
    pbix_ref: string | null
    versao: string | null
  }
  pages: Array<{ codigo: string; nome: string; rota: string; ordem: number }>
  filters: Array<{
    codigo: string
    nome: string
    tipo_filtro: string
    escopo: string
    dataset_code: string
    dependencias_json: unknown[]
    config_json: Record<string, unknown>
  }>
  widgets: Array<{
    codigo: string
    nome: string
    tipo_widget: string
    dataset_code: string
    config_json: Record<string, unknown>
    ordem: number
  }>
}

export interface LastUpdate {
  dashboard_code: string
  latest: {
    dataset_code: string
    last_success_at: string
    row_count: number
    refresh_ref: string
  } | null
  datasets: Array<{
    dataset_code: string
    last_success_at: string
    row_count: number
    refresh_ref: string
  }>
}

export interface WidgetQueryResponse {
  dashboard_code: string
  widget_code: string
  dataset_code: string
  empresa_id: number
  rows: Array<Record<string, unknown>>
  pagination: {
    limit: number
    offset: number
    total_rows: number
  }
  source: string
}

export interface WidgetQueryFilters {
  dtref?: {
    from?: string | null
    to?: string | null
  }
  infiscal?: string
}

export interface WidgetQueryParams {
  filters: WidgetQueryFilters
  limit?: number
  offset?: number
}

// --- Endpoints ---

export const dashboardServiceApi = {
  getBundle: async (code: string = 'TAV3'): Promise<DashboardBundle> => {
    const res = await dsClient.get<DashboardBundle>(`/v1/dashboards/${code}`, { timeout: 15000 })
    return res.data
  },

  getLastUpdate: async (code: string = 'TAV3'): Promise<LastUpdate> => {
    const res = await dsClient.get<LastUpdate>(`/v1/dashboards/${code}/last-update`, { timeout: 15000 })
    return res.data
  },

  queryWidget: async (
    dashboardCode: string,
    widgetCode: string,
    params: WidgetQueryParams
  ): Promise<WidgetQueryResponse> => {
    const res = await dsClient.post<WidgetQueryResponse>(
      `/v1/dashboards/${dashboardCode}/widgets/${widgetCode}`,
      {
        filters: params.filters,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
      { timeout: 30000 }
    )
    return res.data
  },

  exportData: async (
    dashboardCode: string,
    exportCode: string,
    filters: WidgetQueryFilters
  ): Promise<Blob> => {
    const res = await dsClient.post(
      `/v1/dashboards/${dashboardCode}/export`,
      { export_code: exportCode, filters },
      { responseType: 'blob', timeout: 60000 }
    )
    return res.data
  },

  refreshData: async (
    dashboardCode: string = 'TAV3',
    note?: string
  ): Promise<{ dashboard_code: string; refresh_ref: string; datasets: Array<{ dataset_code: string; row_count: number }> }> => {
    const res = await dsClient.post(
      `/v1/internal/refresh/${dashboardCode}`,
      { note: note || 'Refresh via dashboard' },
      { timeout: 300000 }
    )
    return res.data
  },
}
